/**
 * @module Schema
 * @description
 * This module provides a set of functions for defining the schema of a BaseFlow database.
 * It allows you to define tables, fields, and constraints using a simple and intuitive syntax.
 */

/**
 * Defines the structure of a field in a table.
 */
export interface FieldDefinition {
  type: string;
  constraints: {
    primary?: boolean;
    required?: boolean;
    unique?: boolean;
    autoIncrement?: boolean;
    default?: any;
    foreign?: {
      table: string;
      field: string;
    };
  };
}

/**
 * Defines the structure of a table, including its name, fields, and type.
 */
export interface TableDefinition {
  name: string;
  fields: Record<string, FieldDefinition>;
  type: 'table';
}

/**
 * Defines a table with the given name and fields.
 *
 * @param tableName The name of the table.
 * @param fields A record of field names and their definitions.
 * @returns A table definition object.
 */
export function defineTable(tableName: string, fields: Record<string, string>): TableDefinition {
  const parsedFields: Record<string, FieldDefinition> = {};
  let hasPrimaryKey = false;

  // Parse each field definition
  for (const [fieldName, fieldDefinition] of Object.entries(fields)) {
    const parsed = parseFieldDefinition(fieldName, fieldDefinition);
    
    if (parsed.constraints.primary) {
      if (hasPrimaryKey) {
        throw new Error(`Table "${tableName}" cannot have multiple primary keys`);
      }
      hasPrimaryKey = true;
    }

    parsedFields[fieldName] = parsed;
  }

  // Add auto-increment primary key if none specified
  if (!hasPrimaryKey) {
    parsedFields.id = {
      type: 'integer',
      constraints: {
        primary: true,
        autoIncrement: true,
        required: true
      }
    };
  }

  return {
    name: tableName,
    fields: parsedFields,
    type: 'table'
  };
}

/**
 * Parses a field definition string into a `FieldDefinition` object.
 *
 * @param fieldName The name of the field.
 * @param definition The field definition string.
 * @returns A `FieldDefinition` object.
 */
function parseFieldDefinition(fieldName: string, definition: string): FieldDefinition {
  const parts = definition.split('@');
  const type = parts[0].trim();
  const constraintParts = parts.slice(1);

  const constraints: FieldDefinition['constraints'] = {
    primary: false,
    required: false,
    unique: false,
    autoIncrement: false,
    default: null
  };

  // Parse constraints
  for (const constraint of constraintParts) {
    const trimmed = constraint.trim();
    
    if (trimmed === 'primary') {
      constraints.primary = true;
      constraints.required = true;
    } else if (trimmed === 'required') {
      constraints.required = true;
    } else if (trimmed === 'unique') {
      constraints.unique = true;
    } else if (trimmed === 'autoincrement' || trimmed === 'auto_increment') {
      constraints.autoIncrement = true;
    } else if (trimmed.startsWith('default(') && trimmed.endsWith(')')) {
      const defaultValue = trimmed.slice(8, -1);
      if (defaultValue === 'now') {
        constraints.default = 'now';
      } else if (defaultValue === 'true') {
        constraints.default = true;
      } else if (defaultValue === 'false') {
        constraints.default = false;
      } else if (!isNaN(Number(defaultValue))) {
        constraints.default = Number(defaultValue);
      } else {
        constraints.default = defaultValue.replace(/['"]/g, '');
      }
    } else if (trimmed.startsWith('foreign(') && trimmed.endsWith(')')) {
      const foreignRef = trimmed.slice(8, -1);
      const [table, field] = foreignRef.split('.');
      constraints.foreign = { table, field };
    }
  }

  return { type, constraints };
}

/**
 * Validates a field type.
 *
 * @param type The field type to validate.
 * @returns `true` if the field type is valid, `false` otherwise.
 */
export function validateFieldType(type: string): boolean {
  const validTypes = ['text', 'integer', 'real', 'boolean', 'datetime', 'json'];
  return validTypes.includes(type);
}

/**
 * Creates a schema from a record of table definitions.
 *
 * @param tables A record of table definitions.
 * @returns A record of table definitions.
 */
export function createSchema(tables: Record<string, TableDefinition>): Record<string, TableDefinition> {
  return tables;
}